import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, Beaker, Users, Search } from "lucide-react";
import {
  LEARN_CATEGORIES,
  publishedGuides,
  featuredGuides,
  guidesByCategory,
} from "@/data/learnGuides";

const EVIDENCE_TIERS = [
  {
    icon: Beaker,
    label: "Laboratory",
    body: "In-vitro and biochemical studies investigating molecular or cellular mechanisms.",
  },
  {
    icon: FlaskConical,
    label: "Preclinical",
    body: "Animal and experimental models used to investigate biological pathways and compound behaviour.",
  },
  {
    icon: Users,
    label: "Human research",
    body: "Clinical or human research where published evidence exists. Human findings are not inferred from laboratory or animal evidence.",
  },
];

// ---------------------------------------------------------------------------
// /learn, Research Guide Library hub.
//
// This page is the permanent index for every PepDirect Learn/research guide.
// It is intentionally NOT a blog (no dates, no author cards, no chronological
// feed). Guide metadata is read from `src/data/learnGuides.js` so new pages
// can be added in one place. Category buckets are pulled from LEARN_CATEGORIES
// so this component doesn't need to know which topics currently have content
//, empty buckets are skipped automatically.
// ---------------------------------------------------------------------------
export default function LearnIndexPage() {
  const [query, setQuery] = useState("");

  const featured = useMemo(featuredGuides, []);
  const allGuides = useMemo(publishedGuides, []);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allGuides;
    return allGuides.filter((g) =>
      [g.title, g.description, g.topic, g.slug]
        .filter(Boolean)
        .some((f) => f.toLowerCase().includes(q)),
    );
  }, [query, allGuides]);

  useEffect(() => {
    document.title = "Research Peptide Guides & Compound Information | PepDirect";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta(
      "description",
      "Explore PepDirect research guides covering peptides, biochemical compounds, peptide blends and laboratory research topics, with references to scientific literature.",
    );
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://pepdirect.co/learn");

    // JSON-LD: CollectionPage + BreadcrumbList + ItemList of published guides.
    // No Product/Offer schema on the hub, this page is a research library.
    const collection = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Research Peptide Guides",
      description:
        "Educational guides on research peptides, biochemical compounds, peptide blends and laboratory research topics.",
      url: "https://pepdirect.co/learn",
      publisher: { "@type": "Organization", name: "PepDirect", url: "https://pepdirect.co" },
    };
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://pepdirect.co/" },
        { "@type": "ListItem", position: 2, name: "Research Guides", item: "https://pepdirect.co/learn" },
      ],
    };
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "PepDirect Research Guides",
      itemListElement: publishedGuides().map((g, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://pepdirect.co/learn/${g.slug}`,
        name: g.title,
      })),
    };
    const attach = (id, obj) => {
      document.getElementById(id)?.remove();
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      el.textContent = JSON.stringify(obj);
      document.head.appendChild(el);
    };
    attach("learn-hub-collection", collection);
    attach("learn-hub-breadcrumb", breadcrumb);
    attach("learn-hub-itemlist", itemList);
    return () => {
      document.getElementById("learn-hub-collection")?.remove();
      document.getElementById("learn-hub-breadcrumb")?.remove();
      document.getElementById("learn-hub-itemlist")?.remove();
    };
  }, []);

  return (
    <div className="container-pd py-16 lg:py-24" data-testid="learn-index-page">
      <div className="mx-auto max-w-5xl">
        {/* Hero ---------------------------------------------------------- */}
        <p className="label-mono text-brandblue">↳ Research library</p>
        <h1
          className="mt-4 font-heading text-4xl font-black tracking-tight text-ink sm:text-5xl"
          data-testid="learn-index-title"
        >
          Research Peptide Guides
        </h1>
        <p className="mt-6 max-w-3xl text-lg leading-relaxed text-subtle">
          Explore PepDirect&apos;s educational library covering research peptides,
          biochemical compounds, multi-component formulations and the scientific
          literature surrounding them. Each guide is designed to separate compound
          identity, mechanism and published evidence from commercial product
          information.
        </p>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-subtle">
          Research findings are described according to the type of evidence available,
          including laboratory, preclinical and human research where applicable.
        </p>
        <p className="mt-4 rounded-xl border border-brandblue/20 bg-brandblue/5 px-4 py-3 font-mono text-xs uppercase tracking-[0.14em] text-brandblue">
          For laboratory research context only.
        </p>

        {/* Featured ------------------------------------------------------- */}
        {featured.length > 0 && (
          <section className="mt-16" data-testid="learn-index-featured">
            <p className="label-mono text-brandblue">§ Featured guides</p>
            <h2 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
              Start with these research guides
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
              {featured.map((g) => (
                <FeaturedCard key={g.slug} guide={g} />
              ))}
            </div>
          </section>
        )}

        {/* Category buckets ---------------------------------------------- */}
        <section className="mt-16" data-testid="learn-index-categories">
          <p className="label-mono text-brandblue">§ Browse by topic</p>
          <h2 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            Browse by topic
          </h2>

          {/* Metadata-driven topic navigation pills. In-page anchors only
              (no new indexable filtered URLs, no query strings). We keep
              each pill as a native <a href="#anchor"> so it stays
              crawlable and keyboard-accessible, and add a click handler
              that upgrades the jump to a smooth scroll while preserving
              the /learn canonical (History replaceState so the hash
              doesn't push a new entry). Empty categories are filtered
              out so a pill only exists once its section has content. */}
          {(() => {
            const topicNav = LEARN_CATEGORIES.filter(
              (c) => guidesByCategory(c.key).length > 0,
            );
            if (topicNav.length === 0) return null;
            const onPill = (e, anchor) => {
              e.preventDefault();
              const el = document.getElementById(anchor);
              if (!el) return;
              el.scrollIntoView({ behavior: "smooth", block: "start" });
              window.history.replaceState(null, "", `#${anchor}`);
            };
            return (
              <nav
                aria-label="Browse research guides by topic"
                className="mt-6 -mx-1 flex gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:overflow-visible sm:pb-0"
                data-testid="learn-topic-nav"
              >
                {topicNav.map((c) => (
                  <a
                    key={c.anchor}
                    href={`#${c.anchor}`}
                    onClick={(e) => onPill(e, c.anchor)}
                    className="shrink-0 whitespace-nowrap rounded-full border border-brandblue/40 bg-white px-4 py-2 font-mono text-xs font-semibold uppercase tracking-[0.08em] text-brandblue transition-colors hover:bg-brandblue/8 focus:outline-none focus-visible:ring-2 focus-visible:ring-brandblue focus-visible:ring-offset-2"
                    data-testid={`learn-topic-pill-${c.anchor}`}
                  >
                    {c.heading}
                  </a>
                ))}
              </nav>
            );
          })()}

          <div className="mt-8 space-y-10">
            {LEARN_CATEGORIES.map((cat) => {
              const guides = guidesByCategory(cat.key);
              if (guides.length === 0) return null;
              return (
                <div
                  key={cat.key}
                  id={cat.anchor}
                  className="scroll-mt-24"
                  data-testid={`learn-index-cat-${cat.key}`}
                >
                  <h3 className="font-heading text-xl font-black text-ink">{cat.heading}</h3>
                  <p className="mt-2 max-w-3xl text-sm text-subtle">{cat.intro}</p>
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {guides.map((g) => (
                      <GuideCard key={g.slug} guide={g} />
                    ))}
                  </div>
                  {cat.key === "blends" && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-subtle">
                        Research products:
                      </span>
                      {[
                        { to: "/product/glow", label: "GLOW" },
                        { to: "/product/klow", label: "KLOW" },
                        { to: "/product/hulk", label: "HULK" },
                        { to: "/product/wolverine", label: "Wolverine" },
                      ].map((p) => (
                        <Link
                          key={p.to}
                          to={p.to}
                          className="inline-flex items-center rounded-full border border-brandblue/30 bg-white px-3 py-1 font-mono text-[11px] font-medium tracking-[0.04em] text-brandblue hover:bg-brandblue/8"
                        >
                          {p.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* All guides + search ------------------------------------------- */}
        <section className="mt-16" data-testid="learn-index-all">
          <p className="label-mono text-brandblue">§ All research guides</p>
          <div className="mt-3 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
              Browse all guides
            </h2>
            <label className="relative block w-full sm:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" strokeWidth={2} />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search research guides..."
                className="w-full rounded-full border border-black/10 bg-white py-2.5 pl-9 pr-4 text-sm text-ink placeholder:text-subtle focus:border-brandblue focus:outline-none"
                data-testid="learn-index-search"
                aria-label="Search research guides"
              />
            </label>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((g) => (
              <GuideCard key={g.slug} guide={g} />
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="mt-6 text-sm text-subtle">
              No guides match &quot;{query}&quot; yet. Try a broader term.
            </p>
          )}
        </section>

        {/* Evidence tiers ------------------------------------------------- */}
        <section className="mt-16" data-testid="learn-index-evidence">
          <p className="label-mono text-brandblue">§ Editorial standard</p>
          <h2 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            How PepDirect research guides handle evidence
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {EVIDENCE_TIERS.map(({ icon: Icon, label, body }) => (
              <div key={label} className="rounded-xl panel-warm bg-white p-6">
                <Icon className="h-5 w-5 text-brandblue" strokeWidth={1.5} />
                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.16em] text-brandblue">
                  {label}
                </p>
                <p className="mt-2 text-sm text-ink">{body}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 max-w-3xl text-sm text-subtle">
            Each guide should identify the type and limitations of the evidence being
            discussed.
          </p>
        </section>

        {/* Research products CTA ----------------------------------------- */}
        <section className="mt-16" data-testid="learn-index-products-cta">
          <div className="rounded-2xl panel-warm bg-white p-8">
            <p className="label-mono text-brandblue">↳ Research products</p>
            <h2 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
              Looking for research compounds?
            </h2>
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-ink">
              Product pages contain current format, amount, lot and Certificate of
              Analysis information for PepDirect research materials.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/peptides"
                className="btn-pill bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navydark"
                data-testid="learn-index-cta-peptides"
              >
                Browse Research Peptides →
              </Link>
              <Link
                to="/coa"
                className="btn-pill btn-outline px-6 py-3 text-sm font-semibold"
                data-testid="learn-index-cta-coa"
              >
                Browse COA Library →
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function FeaturedCard({ guide }) {
  return (
    <Link
      to={`/learn/${guide.slug}`}
      className="group flex h-full flex-col rounded-xl panel-warm bg-white p-6 transition-colors hover:border-brandblue/40"
      data-testid={`learn-index-featured-${guide.slug}`}
    >
      <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-brandblue">
        {guide.topic}
      </p>
      <h3 className="mt-2 font-heading text-lg font-black text-ink">{guide.title}</h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-subtle">{guide.description}</p>
      <span className="mt-4 font-mono text-xs font-semibold tracking-[0.06em] text-brandblue group-hover:underline">
        Read guide →
      </span>
    </Link>
  );
}

function GuideCard({ guide }) {
  return (
    <Link
      to={`/learn/${guide.slug}`}
      className="group flex h-full flex-col rounded-xl border border-black/10 bg-white p-5 transition-colors hover:border-brandblue/40"
      data-testid={`learn-index-card-${guide.slug}`}
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-brandblue">
        {guide.topic}
      </p>
      <h3 className="mt-1.5 font-heading text-base font-black text-ink">{guide.title}</h3>
      <p className="mt-1.5 flex-1 text-xs leading-relaxed text-subtle">{guide.description}</p>
      <span className="mt-3 font-mono text-[11px] font-semibold tracking-[0.06em] text-brandblue group-hover:underline">
        Read guide →
      </span>
    </Link>
  );
}
